import { localized, msg } from "@lit/localize";
import { Task as LitTask } from "@lit/task";
import type { SlDialog } from "@shoelace-style/shoelace";
import { subscribe } from "@whilecat/core/events/event-service.js";
import { LEVEL_ACTION_REQUIRED } from "@whilecat/core/events/task-events.js";
import type { Action } from "@whilecat/core/navigation/level-navigation.ts";
import { cycle, pairwise, zip } from "@whilecat/core/utils/arrays.ts";
import { isMobile } from "@whilecat/core/utils/screen-utils.js";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";
import { CONTENT } from "@whilecat/generated/content/content-list.ts";
import { isAuthenticated, redirectToLoginOnUnauthorized } from "@whilecat/services/authentication-service.ts";
import type { LevelInfo } from "@whilecat/services/dto/level-info.ts";
import { getTasksToRepeat } from "@whilecat/services/repetition-service.ts";
import { enrollToCourse, leaveCourse } from "@whilecat/services/user-course-service.ts";
import { LitElement, type PropertyValues, css, html, nothing } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { customElement, property, query, state } from "lit/decorators.js";
import { levelsPlacement, pathFromDivs } from "../course-level/visuals.ts";

import "@whilecat/core/loaders/loading-element.ts";
import "@whilecat/core/navigation/level-navigation.ts";
import "@whilecat/core/widgets/progress-bar.js";
import "@whilecat/core/shoe.ts";

@localized()
@customElement("course-levels")
export class CourseLevels extends LitElement {
    static styles = [
        WhilecatStyle,
        css`
            :host {
              display: grid;
              grid-template-columns: 1fr auto 1fr;
              min-height: 400px; /* to allow navigation level float at right position from the beginning */
              margin-left: 100px;
            }

            .level-navigation {
              grid-column: 3 / 4;
              justify-self: end;
            }

          .loader {
            grid-column: 2;
            grid-row: 2;
          }

          sl-dialog.leaveCourseDialog::part(overlay) {
            background-color: rgb(26, 26, 30, 0.7);
            backdrop-filter: blur(5px);
          }

          sl-dialog.leaveCourseDialog::part(panel) {
            margin-left: var(--whilecat-navigation-panel-width);
          }

          sl-dialog.leaveCourseDialog::part(panel) {
            --header-spacing: 24px;
            --body-spacing: 12px;
            --footer-spacing: 12px;
            --width: 500px;
            padding: 20px;
            background-color: var(--whilecat-card-background-color);
            border: 4px solid var(--whilecat-border-colot);
            border-radius: var(--sl-border-radius-x-large);
            font-family: var(--whilecat-default-font);
            border-top: none;
            border-bottom: none;
          }

          sl-dialog.leaveCourseDialog::part(panel):hover {
            border: 4px solid var(--whilecat-primary);
            border-top: none;
            border-bottom: none;
          }

          .levels {
            display: block;
            grid-column: 2 / 3;
            justify-self: center;
          }

          .hidden {
            visibility: hidden;
          }

          svg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
          }

          .page-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding-top: 70px;
          }

          .lessons-row {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas: "left center right";
            gap: 80px;
            margin-bottom: 45px;
            max-width: 900px;
            min-width: 300px;
          }

          a.level-badge {
            display: flex;
            padding: 12px 18px 12px 18px;
            flex-direction: column;
            align-items: center;
            text-align: center;
            justify-content: center;
            border-radius: 24px;
            max-width: 100px;
            min-height: 30px;

            font-family: var(--whilecat-default-font);
            font-size: 14px;
            text-decoration: none;
          }

          .left {
            grid-area: left;
          }

          .center {
            grid-area: center;
          }

          .right {
            grid-area: right;
          }

          .center::after {
              content: '';
              position: absolute;
              top: 100%;
              left: 50%;
              width: 2px;
              height: 90px; /* gap + element height */
              background: #333;
              transform: translateX(-50%);
          }

          .all-completed {
            color: var(--whilecat-light-4);
            background-color: var(--whilecat-primary);
          }

          .completed {
            color: var(--whilecat-light-1);
            background-color: var(--whilecat-dark-3);
          }

          .completed:hover {
            color: var(--whilecat-light-4);
            background-color: var(--whilecat-primary);
          }

          .all-completed:hover {
            background-color: var(--whilecat-primary-light);
          }

          .in-progress:hover {
            background-color: var(--whilecat-primary-light);
          }

          .in-progress {
            color: var(--whilecat-light-4);
            background-color: var(--whilecat-primary);
          }

          .repetition {
            color: var(--whilecat-light-4);
            background-color: var(--whilecat-primary);
          }

          .repetition:hover {
            background-color: var(--whilecat-primary-light);
          }

          .not-completed {
            color: var(--whilecat-light-2);
            background-color: var(--whilecat-primary-dark-2);
          }

          .course-banner {
            position: relative;
            display: flex;
            padding: 24px;
            flex-direction: column;
            align-items: flex-start;
            align-self: stretch;

            border-radius: 24px;
            background: var(--whilecat-card-background-color);

            font-size: 12px;

            overflow: hidden;
          }

          p.description {
              max-width: 70%;
              font-size: 16px;
          }
          .background-icon {
            top: 20%;
            left: 70%;
            position: absolute;
            color: var(--whilecat-primary);
            font-size: 160px;
            opacity: 0.3;
          }
        `,
        css`
          @media only screen and (max-width: 1200px) {
            :host {
              display: block;
              min-height: 400px; /* to allow navigation level float at right position from the beginning */
              margin-left: 0px; /* override the default */
            }

            .level-navigation {
              visibility: hidden;
            }

            a.level-badge {
              min-width: 85px;
              height: 50px;
            }

            .lessons-row {
              gap: 40px;
            }
          }
        `,
    ];

    @query("#leaveCourseDialog") leaveCourseDialog!: SlDialog;

    @property() courseId!: string;
    @property({ type: Array }) levels!: LevelInfo[];
    @property() courseName!: string;
    @property() courseDescription!: string;
    @property() iconPath!: string;
    @property({ type: Boolean }) enrolled!: boolean;
    @state() loading = true;

    @state() path = "";
    pathsDrawn = false;

    constructor() {
        super();

        subscribe(LEVEL_ACTION_REQUIRED, async (event: { detail: Action }) => {
            redirectToLoginOnUnauthorized();

            switch (event.detail.id) {
                case "LEAVE": {
                    this.leaveCourseDialog.show();
                    return;
                }
                case "ENROLL": {
                    const { response } = await enrollToCourse(this.courseId);
                    if (response.ok) {
                        location.reload();
                    }
                    return;
                }
                default: {
                    console.error("Unknown action: ", event.detail);
                    return;
                }
            }
        });

        window.addEventListener("resize", () => this.updatePathBetweenLessons());
    }

    updated(_changedProperties: PropertyValues<this>): void {
        this.updatePathBetweenLessons();

        // First we draw the lessons' boxes, and then we use their positions to draw
        // SVGs (paths and chips).  We don't want to show lessons until we've drawn
        // the SVGs; this flag shows us that we're ready.
        this.pathsDrawn = true;
    }

    async leaveCourse() {
        redirectToLoginOnUnauthorized();
        const { response } = await leaveCourse(this.courseId);
        if (response.ok) {
            location.reload();
        }
    }

    getProgress() {
        if (this.enrolled && isMobile()) {
            return html`
              <progress-bar .completedLessons="${this.getCompletedCount()}"
                            .totalLessons="${this.levels.length}"
              >
              </progress-bar>
            `;
        }
        return nothing;
    }

    makeLevels(levelNames: Record<string, string>, toRepeat: { length: number }) {
        const nextLevelIdx = this.levels.findIndex(lvl => !lvl.completed);

        const levels = this.levels.map((lvl, i) => {
            return {
                ...lvl,
                previousCompleted: this.levels[i - 1]?.completed ?? true,
                name: levelNames[lvl.sourcePath],
                repetition: i === nextLevelIdx && toRepeat.length > 0,
                courseId: this.courseId,
            };
        });

        // Create rows and fill them with lessons.
        const rows = [];
        for (const row of cycle(levelsPlacement)) {
            const lessons = levels.splice(0, row.length);
            if (lessons.length === 0) break;

            rows.push(zip(lessons, row).map(([level, placement]) => ({ level, placement })));
        }

        return html`
          <div class="course-banner">
            <h3>
              <sl-icon class="course-icon" name="${this.iconPath}"></sl-icon>
              ${this.courseName}
            </h3>
            <p class="description">${this.courseDescription}</p>
            <sl-icon class="background-icon" name="${this.iconPath}"></sl-icon>
            ${this.getProgress()}
          </div>
          <div class="lessons-row"></div>

          ${rows.map(
              row =>
                  html`
                            <div class="lessons-row">
                              ${row.map(data => this.makeLevelElement(data))}
                            </div>`,
          )}
        `;
    }

    getCompletedCount() {
        return this.levels.filter(value => value.completed).length;
    }

    getActions(): Action[] {
        return [this.enrolled ? { id: "LEAVE", name: msg("Leave the course") } : { id: "ENROLL", name: msg("Enroll") }];
    }

    _loader = new LitTask(this, {
        task: async () => {
            const levelNames: Record<string, string> = {};
            for (const lvl of this.levels) {
                levelNames[lvl.sourcePath] = CONTENT()
                    .find(c => c.uuid === this.courseId)!
                    .lessons.find(l => l.path === lvl.sourcePath)!.name;
            }

            const toRepeat = isAuthenticated() ? await getTasksToRepeat(this.courseId) : [];

            return [this.makeLevels(levelNames, toRepeat)];
        },
        args: () => [],
    });

    makeLevelElement(data: {
        level: LevelInfo & { courseId: string; previousCompleted: boolean; name: string; repetition: boolean };
        placement: "left" | "center" | "right";
    }) {
        const { level, placement } = data;
        let classes = placement;

        if (level.repetition) {
            const link = `/repetition?course=${level.courseId}`;
            return html`
              <a class="${classes} level-badge repetition" href=${link}>
                ${msg("Repetition")}
              </a>
            `;
        }

        const wholeCourseCompleted = this.getCompletedCount() == this.levels.length;

        if (wholeCourseCompleted) {
            classes += " all-completed";
        } else if (level.completed) {
            classes += " completed";
        } else if (!level.completed && level.previousCompleted) {
            classes += " in-progress";
        } else {
            classes += " not-completed";
        }

        const available = level.previousCompleted || level.completed;
        const link = available ? window.location.origin + level.levelUrl : undefined;

        return html`
          <a href=${ifDefined(link)} class="level-badge ${classes}">
            ${level.name}
          </a>
        `;
    }

    updatePathBetweenLessons() {
        const levelElements: Array<HTMLDivElement> = Array.from(this.shadowRoot!.querySelectorAll("a.level-badge"));

        this.path = pairwise(levelElements)
            .map(([first, second]) => pathFromDivs(first, second))
            .join("");
    }

    render() {
        return this._loader.render({
            complete: levels => html`
              <svg
                      id="connector"
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
              >
                 <path
                         d=${this.path}
                         fill="none"
                         stroke="var(--whilecat-subcolor-1)"
                         stroke-width="2"
                 ></path>
               </svg>

               <div id="levels" class="levels ${this.pathsDrawn ? "" : "hidden"}">${levels}</div>

               <level-navigation
                       class="level-navigation"
                       .completedCount="${this.getCompletedCount()}"
                       .totalCount="${this.levels.length}"
                       .navigationAnchors="${[]}"
                       .actions="${this.getActions()}"
               >
               </level-navigation>

               <sl-dialog id="leaveCourseDialog" ?noHeader="${true}" class="leaveCourseDialog">
                 <h3>
                   <sl-icon name="question-octagon"
                           style="font-size: 14px; color: #f5a623;"
                   ></sl-icon>
                   ${msg("Are you sure you want to leave?")}
                 </h3>
                 <p>${msg("Your progress will be lost...")}</p>
                 <sl-button
                         slot="footer"
                         @click="${async () => this.leaveCourse()}"
                 >
                   ${msg("Yes")}
                 </sl-button>
                 <sl-button
                         slot="footer"
                         variant="primary"
                         @click="${() => this.leaveCourseDialog.hide()}"
                 >
                     ${msg("Cancel")}
                 </sl-button>
               </sl-dialog>
            `,
        });
    }
}
